import React, { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useApp } from "@/context/AppContext";
import { formatSAR } from "@/i18n";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Banknote, ShieldCheck, Loader2, CheckCircle2, XCircle, Plus, Calendar } from "lucide-react";

export default function FinancingModal({ open, onClose, onSuccess }) {
  const { t, lang } = useApp();
  const [config, setConfig] = useState(null);
  const [list, setList] = useState([]);
  const [view, setView] = useState("home"); // home | nafath | done | rejected
  const [amount, setAmount] = useState(null);
  const [provider, setProvider] = useState("tabby");
  const [appData, setAppData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [doneFin, setDoneFin] = useState(null);
  const pollRef = useRef(null);

  const loadList = () => api.get("/financing").then((r) => setList(r.data)).catch(() => {});

  useEffect(() => {
    if (open) {
      setView("home"); setAmount(null); setProvider("tabby"); setAppData(null); setDoneFin(null);
      api.get("/financing/config").then((r) => setConfig(r.data)).catch(() => {});
      loadList();
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [open]);

  const apply = async () => {
    if (!amount) return;
    setLoading(true);
    try {
      const r = await api.post("/financing/apply", { provider, amount });
      setAppData(r.data);
      setView("nafath");
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  // Poll Nafath status while verifying
  useEffect(() => {
    if (view !== "nafath" || !appData) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/financing/${appData.financing_id}/status`);
        if (r.data.status === "active") {
          clearInterval(pollRef.current);
          setDoneFin(r.data); setView("done"); loadList(); onSuccess?.();
        } else if (r.data.status === "rejected") {
          clearInterval(pollRef.current);
          setView("rejected");
        }
      } catch {}
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [view, appData]);

  const repay = async (financing_id, seq) => {
    setLoading(true);
    try {
      const r = await api.post("/financing/repay", { financing_id, seq });
      setList((prev) => prev.map((f) => (f.financing_id === financing_id ? r.data : f)));
      toast.success(t.toastCardUpdated ? t.repay : "OK");
      onSuccess?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  const providerName = (id) => {
    const p = config?.providers?.find((x) => x.id === id);
    return p ? (lang === "ar" ? p.name_ar : p.name_en) : id;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="financing-modal" className="max-w-sm rounded-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Banknote className="w-5 h-5 text-primary" /> {t.financing}
          </DialogTitle>
          <DialogDescription>{t.financingDesc}</DialogDescription>
        </DialogHeader>

        {!config ? (
          <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : view === "nafath" ? (
          <div className="space-y-4 pt-2 text-center" data-testid="financing-nafath-view">
            <div className="w-14 h-14 rounded-2xl bg-primary/10 grid place-items-center mx-auto">
              <ShieldCheck className="w-7 h-7 text-primary" />
            </div>
            <div>
              <p className="font-bold">{t.nafathTitle}</p>
              <p className="text-sm text-muted-foreground mt-1">{t.nafathInstruction}</p>
            </div>
            <div className="rounded-2xl bg-gradient-to-br from-emerald-950 to-emerald-800 text-white py-6">
              <p className="text-xs text-white/70">{t.selectThisNumber}</p>
              <p data-testid="nafath-random" className="text-6xl font-black tracking-widest mt-1">{appData?.nafath?.random}</p>
            </div>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" /> {t.verifying}
            </div>
            {appData?.nafath?.simulated && (
              <p className="text-[11px] text-amber-600 font-semibold">{t.simulationMode}</p>
            )}
          </div>
        ) : view === "done" ? (
          <div className="space-y-4 pt-2 text-center" data-testid="financing-done-view">
            <div className="w-16 h-16 rounded-full bg-primary/15 grid place-items-center mx-auto">
              <CheckCircle2 className="w-9 h-9 text-primary" />
            </div>
            <div>
              <p className="text-lg font-black">{t.financingApproved}</p>
              <p className="text-3xl font-black tabular-nums text-primary mt-1">+{formatSAR(doneFin?.principal, lang)} <span className="text-base">{t.currency}</span></p>
              <p className="text-sm text-muted-foreground mt-1">{t.cashAddedDesc}</p>
            </div>
            <div className="rounded-2xl bg-muted/50 border border-border p-3 text-sm flex items-center justify-between">
              <span className="text-muted-foreground">{t.monthlyInstallment}</span>
              <span className="font-bold tabular-nums">{formatSAR(doneFin?.installment_amount, lang)} {t.currency} × {doneFin?.installments}</span>
            </div>
            <Button data-testid="financing-done-btn" onClick={onClose} className="w-full h-12 rounded-2xl font-bold">{t.save}</Button>
          </div>
        ) : view === "rejected" ? (
          <div className="space-y-4 pt-2 text-center" data-testid="financing-rejected-view">
            <div className="w-16 h-16 rounded-full bg-destructive/15 grid place-items-center mx-auto">
              <XCircle className="w-9 h-9 text-destructive" />
            </div>
            <p className="font-bold">{t.financingRejected}</p>
            <Button onClick={() => setView("home")} variant="outline" className="w-full h-12 rounded-2xl">{t.tryAgain}</Button>
          </div>
        ) : (
          <div className="space-y-5 pt-1">
            {list.length > 0 && (
              <div className="space-y-3" data-testid="active-financing-list">
                <p className="text-sm font-bold text-muted-foreground uppercase tracking-wide">{t.activeFinancing}</p>
                {list.map((f) => {
                  const next = (f.schedule || []).find((s) => !s.paid);
                  return (
                    <div key={f.financing_id} data-testid={`financing-${f.financing_id}`} className="rounded-2xl border border-border bg-card p-4">
                      <div className="flex items-center justify-between">
                        <span className="font-bold">{providerName(f.provider)} · {formatSAR(f.principal, lang)} {t.currency}</span>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${f.status === "completed" ? "bg-primary/15 text-primary" : "bg-amber-500/15 text-amber-600"}`}>
                          {f.status === "completed" ? t.allPaid : `${t.remaining} ${formatSAR(f.remaining_balance, lang)}`}
                        </span>
                      </div>
                      <div className="flex gap-1.5 mt-3">
                        {(f.schedule || []).map((s) => (
                          <div key={s.seq} className={`flex-1 h-1.5 rounded-full ${s.paid ? "bg-primary" : "bg-muted"}`} />
                        ))}
                      </div>
                      {next && (
                        <div className="flex items-center justify-between mt-3">
                          <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                            <Calendar className="w-3.5 h-3.5" /> {t.installment} {next.seq}/{f.installments} · {formatSAR(next.amount, lang)} {t.currency}
                          </span>
                          <Button data-testid={`repay-${f.financing_id}`} size="sm" disabled={loading} onClick={() => repay(f.financing_id, next.seq)} className="h-8 rounded-xl text-xs font-bold">
                            {t.repay}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="space-y-3">
              <p className="text-sm font-bold">{t.chooseAmount}</p>
              <div className="grid grid-cols-3 gap-2">
                {config.offers.map((a) => (
                  <button key={a} data-testid={`financing-amount-${a}`} onClick={() => setAmount(a)}
                    className={`py-3 rounded-2xl border font-bold tabular-nums transition-all ${amount === a ? "border-primary bg-primary/10 text-primary" : "border-border bg-card"}`}>
                    {a.toLocaleString(lang === "ar" ? "ar-SA" : "en-US")}
                  </button>
                ))}
              </div>

              <p className="text-sm font-bold pt-1">{t.chooseProvider}</p>
              <div className="grid grid-cols-2 gap-2">
                {config.providers.map((p) => (
                  <button key={p.id} data-testid={`financing-provider-${p.id}`} onClick={() => setProvider(p.id)}
                    className={`py-3 rounded-2xl border font-bold transition-all ${provider === p.id ? "border-primary bg-primary/10 text-primary" : "border-border bg-card"}`}>
                    {lang === "ar" ? p.name_ar : p.name_en}
                  </button>
                ))}
              </div>

              {amount && (
                <div className="rounded-2xl bg-muted/50 border border-border p-3.5 text-sm space-y-1.5" data-testid="financing-summary">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">{t.monthlyInstallment}</span>
                    <span className="font-bold tabular-nums">{formatSAR(amount / config.installments, lang)} {t.currency} × {config.installments}</span>
                  </div>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>{t.installmentsPlan}</span><span className="text-primary font-bold">{t.zeroFee}</span>
                  </div>
                </div>
              )}

              <Button data-testid="financing-apply-btn" onClick={apply} disabled={loading || !amount} className="w-full h-[52px] rounded-2xl text-base font-bold">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Plus className="w-4 h-4 me-1.5" />{t.applyNow}</>}
              </Button>
              <p className="text-[11px] text-center text-muted-foreground flex items-center justify-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" /> {t.poweredByNafath}
              </p>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
